import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import { SidebarProvider, useSidebar } from "../../components/ui/Sidebar";
import Sidebar from "../../components/ui/Sidebar";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import InviteUserModal from "./components/InviteUserModal";

import { getAuthenticatedUser } from "api/auth.api";
import { getBranches } from "api/branch.api";
import {
  createInvitation,
  getInvitations,
  resendInvitation,
  revokeInvitation,
} from "api/invitation.api";
import {
  getMemberships,
  removeMembership,
  updateMembership,
} from "api/membership.api";
import { QUERY_KEYS } from "config/queryKeys";

const COMPANY_ROLE_OPTIONS = [
  { value: "COMPANY_ADMIN", label: "Company Admin" },
  { value: "MEMBER", label: "Branch User" },
];

const BRANCH_ROLE_OPTIONS = [
  { value: "BRANCH_MANAGER", label: "Branch Manager" },
  { value: "ACCOUNTANT", label: "Accountant" },
  { value: "VIEWER", label: "Viewer" },
];

const EMPTY_BRANCH_ROLE = {
  branchId: "",
  branchRole: "ACCOUNTANT",
};

const queryKeys = {
  authMe: () => QUERY_KEYS?.auth?.me?.() || ["auth", "me"],

  branchesList: () => QUERY_KEYS?.branches?.list?.() || ["branches", "list"],

  membershipsList: () =>
    QUERY_KEYS?.memberships?.list?.() || ["memberships", "list"],

  membershipsAll: () => QUERY_KEYS?.memberships?.all || ["memberships"],

  invitationsList: (params) =>
    QUERY_KEYS?.invitations?.list?.(params) || ["invitations", "list", params],

  invitationsAll: () => QUERY_KEYS?.invitations?.all || ["invitations"],
};

const getPermissions = (authData) => {
  return (
    authData?.activeCompany?.permissions ||
    authData?.user?.activeCompany?.permissions ||
    authData?.data?.activeCompany?.permissions ||
    []
  );
};

const hasPermission = (permissions, permission) => {
  if (!Array.isArray(permissions) || permissions.length === 0) return true;
  return permissions.includes(permission);
};

const getMembersArray = (data) => {
  const members = data?.data?.members || data?.members || data?.data || [];
  return Array.isArray(members) ? members : [];
};

const getInvitationsArray = (data) => {
  const invitations =
    data?.data?.invitations || data?.invitations || data?.data || [];
  return Array.isArray(invitations) ? invitations : [];
};

const getBranchesArray = (data) => {
  if (Array.isArray(data)) return data;
  const branches = data?.data?.branches || data?.branches || data?.data || [];
  return Array.isArray(branches) ? branches : [];
};

const getMemberId = (member) => member?._id || member?.id;
const getInvitationId = (invitation) => invitation?._id || invitation?.id;
const getBranchId = (branch) => {
  if (!branch) return "";
  if (typeof branch === "string") return branch;
  return branch?._id || branch?.id || "";
};

const getBranchName = (branch) => {
  if (!branch) return "Unnamed branch";
  if (typeof branch === "string") return branch;
  return branch?.name || branch?.branchName || branch?.code || "Unnamed branch";
};

const getBranchLabelFromAccess = (item) => {
  if (!item) return "Branch";

  const branchId = item.branchId;

  if (branchId && typeof branchId === "object") {
    return (
      branchId.name ||
      branchId.branchName ||
      branchId.code ||
      branchId.branchCode ||
      branchId._id ||
      branchId.id ||
      "Branch"
    );
  }

  return (
    item.branchName ||
    item.branchCode ||
    item.branch?.name ||
    item.branch?.code ||
    branchId ||
    "Branch"
  );
};

const formatDate = (date) => {
  if (!date) return "—";

  const parsed = new Date(date);

  if (Number.isNaN(parsed.getTime())) {
    return "—";
  }

  return parsed.toLocaleDateString();
};

const formatRole = (role) => {
  if (!role) return "—";

  return String(role)
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const RoleBadge = ({ children }) => (
  <span className="inline-flex rounded-full px-2 py-1 text-xs font-medium bg-primary/10 text-primary">
    {children}
  </span>
);

const StatusBadge = ({ status }) => {
  const value = status || "UNKNOWN";

  const className =
    value === "ACTIVE" || value === "ACCEPTED"
      ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
      : value === "PENDING"
        ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300"
        : "bg-muted text-muted-foreground";

  return (
    <span
      className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${className}`}
    >
      {formatRole(value)}
    </span>
  );
};

const BranchAccessList = ({ items = [], companyRole }) => {
  if (companyRole === "COMPANY_ADMIN") {
    return <span className="text-muted-foreground">All branches</span>;
  }

  if (!Array.isArray(items) || items.length === 0) {
    return <span className="text-muted-foreground">No branch access</span>;
  }

  return (
    <div className="space-y-1">
      {items.map((item, index) => {
        const label = getBranchLabelFromAccess(item);
        const keyBranchId =
          typeof item?.branchId === "object"
            ? item.branchId?._id || item.branchId?.id
            : item?.branchId;

        return (
          <div
            key={`${keyBranchId || index}-${item.branchRole || "role"}`}
            className="text-xs"
          >
            <span className="font-medium text-foreground">{String(label)}</span>
            <span className="text-muted-foreground">
              {" "}
              — {formatRole(item.branchRole)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const EditMemberModal = ({
  member,
  branches,
  onClose,
  onSubmit,
  isSubmitting,
}) => {
  const [companyRole, setCompanyRole] = useState(
    member?.companyRole === "COMPANY_ADMIN" ? "COMPANY_ADMIN" : "MEMBER",
  );

  const [branchRoles, setBranchRoles] = useState(() => {
    if (member?.companyRole === "COMPANY_ADMIN") return [];

    const existing = member?.branchAccess || [];
    if (existing.length === 0) return [{ ...EMPTY_BRANCH_ROLE }];

    return existing.map((access) => ({
      branchId: getBranchId(access.branchId) || access.branchId || "",
      branchRole: access.branchRole || "ACCOUNTANT",
    }));
  });

  const [errors, setErrors] = useState({});

  const branchOptions = useMemo(
    () =>
      getBranchesArray(branches)
        .map((branch) => ({
          id: getBranchId(branch),
          name: getBranchName(branch),
          code: branch?.code || branch?.branchCode || "",
        }))
        .filter((branch) => branch.id),
    [branches],
  );

  const updateBranchRole = (index, field, value) => {
    setBranchRoles((previous) =>
      previous.map((role, roleIndex) =>
        roleIndex === index ? { ...role, [field]: value } : role,
      ),
    );
  };

  const addBranchAccess = () => {
    setBranchRoles((previous) => [...previous, { ...EMPTY_BRANCH_ROLE }]);
  };

  const removeBranchAccess = (index) => {
    setBranchRoles((previous) =>
      previous.filter((_, roleIndex) => roleIndex !== index),
    );
  };

  const validate = () => {
    const nextErrors = {};

    if (!["COMPANY_ADMIN", "MEMBER"].includes(companyRole)) {
      nextErrors.companyRole = "Select a valid role.";
    }

    if (companyRole === "MEMBER") {
      if (branchOptions.length === 0) {
        nextErrors.branchRoles =
          "Create a branch before assigning branch-level access.";
      } else if (!branchRoles.length) {
        nextErrors.branchRoles = "Add at least one branch access.";
      } else {
        const seen = new Set();

        branchRoles.forEach((role, index) => {
          if (!role.branchId) {
            nextErrors[`branchId_${index}`] = "Select a branch.";
          }

          if (!role.branchRole) {
            nextErrors[`branchRole_${index}`] = "Select a branch role.";
          }

          if (role.branchId && seen.has(role.branchId)) {
            nextErrors[`branchId_${index}`] =
              "This branch is already assigned.";
          }

          if (role.branchId) seen.add(role.branchId);
        });
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!validate()) return;

    const payload =
      companyRole === "COMPANY_ADMIN"
        ? {
            companyRole: "COMPANY_ADMIN",
            branchRoles: [],
          }
        : {
            companyRole: "MEMBER",
            branchRoles: branchRoles.map((role) => ({
              branchId: role.branchId,
              branchRole: role.branchRole,
            })),
          };

    onSubmit(payload);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[10000] flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              Edit Member Access
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Update role and branch access for{" "}
              {member?.user?.email || "member"}.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Company role
            </label>
            <select
              value={companyRole}
              onChange={(event) => {
                const value = event.target.value;
                setCompanyRole(value);
                setErrors({});

                if (value === "COMPANY_ADMIN") {
                  setBranchRoles([]);
                }

                if (value === "MEMBER" && branchRoles.length === 0) {
                  setBranchRoles([{ ...EMPTY_BRANCH_ROLE }]);
                }
              }}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              {COMPANY_ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors.companyRole && (
              <p className="text-xs text-destructive mt-1">
                {errors.companyRole}
              </p>
            )}
          </div>

          {companyRole === "COMPANY_ADMIN" ? (
            <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-muted-foreground">
              Company Admin has access to all branches.
            </div>
          ) : (
            <div className="border-t border-border pt-5">
              <div className="flex items-center justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">
                    Branch access
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    Assign one or more branches and roles.
                  </p>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addBranchAccess}
                  disabled={branchOptions.length === 0}
                >
                  Add Branch Access
                </Button>
              </div>

              {branchOptions.length === 0 ? (
                <div className="rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-muted-foreground">
                  Create a branch before assigning branch-level users.
                </div>
              ) : (
                <div className="space-y-3">
                  {branchRoles.map((role, index) => (
                    <div
                      key={`${role.branchId || "branch"}-${index}`}
                      className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-start"
                    >
                      <div>
                        <select
                          value={role.branchId}
                          onChange={(event) =>
                            updateBranchRole(
                              index,
                              "branchId",
                              event.target.value,
                            )
                          }
                          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                        >
                          <option value="">Select branch</option>
                          {branchOptions.map((branch) => (
                            <option key={branch.id} value={branch.id}>
                              {branch.name}
                              {branch.code ? ` (${branch.code})` : ""}
                            </option>
                          ))}
                        </select>
                        {errors[`branchId_${index}`] && (
                          <p className="text-xs text-destructive mt-1">
                            {errors[`branchId_${index}`]}
                          </p>
                        )}
                      </div>

                      <div>
                        <select
                          value={role.branchRole}
                          onChange={(event) =>
                            updateBranchRole(
                              index,
                              "branchRole",
                              event.target.value,
                            )
                          }
                          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                        >
                          {BRANCH_ROLE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeBranchAccess(index)}
                        disabled={branchRoles.length === 1}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {errors.branchRoles && (
                <p className="text-xs text-destructive mt-2">
                  {errors.branchRoles}
                </p>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const UserManagementContent = () => {
  const { isCollapsed } = useSidebar();
  const { t } = useTranslation("pages");
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState("members");
  const [invitationStatus, setInvitationStatus] = useState("");
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);

  const invitationParams = useMemo(
    () => ({
      ...(invitationStatus ? { status: invitationStatus } : {}),
    }),
    [invitationStatus],
  );

  const { data: authData } = useQuery({
    queryKey: queryKeys.authMe(),
    queryFn: getAuthenticatedUser,
    retry: false,
    staleTime: 1000 * 60 * 5,
  });

  const permissions = getPermissions(authData);
  const canInvite = hasPermission(permissions, "user:invite");
  const canManage = hasPermission(permissions, "user:manage");
  const canViewPage = canInvite || canManage;

  const { data: branchesData } = useQuery({
    queryKey: queryKeys.branchesList(),
    queryFn: () => getBranches(),
    select: getBranchesArray,
    staleTime: 1000 * 60 * 30,
    enabled: canViewPage,
  });

  const branches = branchesData || [];

  const {
    data: membersData,
    isLoading: isMembersLoading,
    isError: isMembersError,
    error: membersError,
  } = useQuery({
    queryKey: queryKeys.membershipsList(),
    queryFn: () => getMemberships(),
    retry: false,
    enabled: canViewPage,
  });

  const {
    data: invitationsData,
    isLoading: isInvitationsLoading,
    isError: isInvitationsError,
    error: invitationsError,
  } = useQuery({
    queryKey: queryKeys.invitationsList(invitationParams),
    queryFn: () => getInvitations(invitationParams),
    retry: false,
    enabled: canViewPage,
  });

  const members = getMembersArray(membersData);
  const invitations = getInvitationsArray(invitationsData);

  const invalidateUserManagement = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.membershipsAll() });
    queryClient.invalidateQueries({ queryKey: queryKeys.invitationsAll() });
    queryClient.invalidateQueries({ queryKey: queryKeys.authMe() });
  };

  const inviteMutation = useMutation({
    mutationFn: createInvitation,
    onSuccess: () => {
      invalidateUserManagement();
      toast.success("Invitation sent successfully.");
      setIsInviteModalOpen(false);
    },
    onError: (error) => {
      toast.error(error?.message || "Failed to send invitation.");
    },
  });

  const updateMemberMutation = useMutation({
    mutationFn: ({ id, body }) => updateMembership(id, body),
    onSuccess: () => {
      invalidateUserManagement();
      toast.success("Member access updated.");
      setEditingMember(null);
    },
    onError: (error) => {
      toast.error(error?.message || "Failed to update member.");
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: removeMembership,
    onSuccess: () => {
      invalidateUserManagement();
      toast.success("Member removed from company.");
    },
    onError: (error) => {
      toast.error(error?.message || "Failed to remove member.");
    },
  });

  const resendInvitationMutation = useMutation({
    mutationFn: resendInvitation,
    onSuccess: () => {
      invalidateUserManagement();
      toast.success("Invitation resent.");
    },
    onError: (error) => {
      toast.error(error?.message || "Failed to resend invitation.");
    },
  });

  const revokeInvitationMutation = useMutation({
    mutationFn: revokeInvitation,
    onSuccess: () => {
      invalidateUserManagement();
      toast.success("Invitation revoked.");
    },
    onError: (error) => {
      toast.error(error?.message || "Failed to revoke invitation.");
    },
  });

  const handleInviteUser = async (payload) => {
    await inviteMutation.mutateAsync(payload);
  };

  const handleRemoveMember = (member) => {
    const memberId = getMemberId(member);

    if (!memberId) {
      toast.error("Could not identify member.");
      return;
    }

    if (member.companyRole === "OWNER") {
      toast.error("Owner cannot be removed from this screen.");
      return;
    }

    const confirmed = window.confirm(
      `Remove ${member?.user?.email || "this member"} from the company?`,
    );

    if (!confirmed) return;

    removeMemberMutation.mutate(memberId);
  };

  const handleSaveMember = (payload) => {
    const memberId = getMemberId(editingMember);

    if (!memberId) {
      toast.error("Could not identify member.");
      return;
    }

    updateMemberMutation.mutate({
      id: memberId,
      body: payload,
    });
  };

  const handleResendInvitation = (invitation) => {
    const invitationId = getInvitationId(invitation);

    if (!invitationId) {
      toast.error("Could not identify invitation.");
      return;
    }

    resendInvitationMutation.mutate(invitationId);
  };

  const handleRevokeInvitation = (invitation) => {
    const invitationId = getInvitationId(invitation);

    if (!invitationId) {
      toast.error("Could not identify invitation.");
      return;
    }

    const confirmed = window.confirm(
      `Revoke invitation for ${invitation?.email || "this user"}?`,
    );

    if (!confirmed) return;

    revokeInvitationMutation.mutate(invitationId);
  };

  const isLoading = isMembersLoading || isInvitationsLoading;

  if (isLoading) {
    return <LoadingContent isCollapsed={isCollapsed} />;
  }

  return (
    <div
      className={`transition-all duration-300 ease-in-out ${
        isCollapsed ? "md:ms-16" : "md:ms-64"
      }`}
    >
      <main className="p-4 md:p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                {t("users.title")}
              </h1>
              <p className="text-muted-foreground">
                {t("users.description")}
              </p>
            </div>

            {canInvite && (
              <Button
                onClick={() => setIsInviteModalOpen(true)}
                iconName="UserPlus"
                iconPosition="left"
              >
                {t("users.inviteUser")}
              </Button>
            )}
          </div>
        </motion.div>

        {!canViewPage ? (
          <div className="bg-card border border-border rounded-xl p-8 text-center">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon
                name="ShieldAlert"
                size={32}
                className="text-muted-foreground"
              />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {t("branches.permissionRequired")}
            </h3>
            <p className="text-muted-foreground">
              {t("branches.permissionDescription")}
            </p>
          </div>
        ) : (
          <>
            <div className="bg-card rounded-xl p-2 shadow-sm border border-border mb-6 inline-flex gap-2">
              <button
                type="button"
                onClick={() => setActiveTab("members")}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === "members"
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted"
                }`}
              >
                {t("users.members", { defaultValue: "Members" })}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("invitations")}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === "invitations"
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted"
                }`}
              >
                {t("users.invitations", { defaultValue: "Invitations" })}
              </button>
            </div>

            {activeTab === "members" && (
              <MembersTable
                members={members}
                isError={isMembersError}
                error={membersError}
                canManage={canManage}
                onEdit={setEditingMember}
                onRemove={handleRemoveMember}
                isRemoving={removeMemberMutation.isPending}
              />
            )}

            {activeTab === "invitations" && (
              <InvitationsTable
                invitations={invitations}
                isError={isInvitationsError}
                error={invitationsError}
                status={invitationStatus}
                onStatusChange={setInvitationStatus}
                onResend={handleResendInvitation}
                onRevoke={handleRevokeInvitation}
                isResending={resendInvitationMutation.isPending}
                isRevoking={revokeInvitationMutation.isPending}
              />
            )}
          </>
        )}

        <InviteUserModal
          isOpen={isInviteModalOpen}
          onClose={() => setIsInviteModalOpen(false)}
          onInvite={handleInviteUser}
          branches={branches}
        />

        {editingMember && (
          <EditMemberModal
            member={editingMember}
            branches={branches}
            onClose={() => setEditingMember(null)}
            onSubmit={handleSaveMember}
            isSubmitting={updateMemberMutation.isPending}
          />
        )}
      </main>
    </div>
  );
};

const MembersTable = ({
  members,
  isError,
  error,
  canManage,
  onEdit,
  onRemove,
  isRemoving,
}) => {
  const { t } = useTranslation("pages");

  if (isError) {
    const isForbidden =
      error?.status === 403 || error?.response?.status === 403;

    return (
      <div className="bg-card border border-border rounded-xl p-8 text-center">
        <Icon
          name={isForbidden ? "ShieldAlert" : "AlertTriangle"}
          size={32}
          className="mx-auto text-muted-foreground mb-3"
        />
        <p className="text-muted-foreground">
          {isForbidden
            ? t("users.membersForbidden", { defaultValue: "You do not have permission to view members." })
            : error?.message || t("users.membersFailed", { defaultValue: "Failed to load members." })}
        </p>
      </div>
    );
  }

  if (members.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-12 text-center">
        <Icon
          name="Users"
          size={32}
          className="mx-auto text-muted-foreground mb-3"
        />
        <h3 className="text-lg font-semibold text-foreground mb-2">
          {t("users.noMembers")}
        </h3>
        <p className="text-muted-foreground">
          {t("users.membersEmptyDescription", { defaultValue: "Members will appear here once users join this company." })}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
      <div className="p-6 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground">
          {t("users.members", { defaultValue: "Members" })}
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          {t("users.membersDescription", { defaultValue: "Active users in the selected company." })}
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-muted-foreground">
            <tr>
              <th className="text-start px-6 py-3 font-medium">{t("users.user")}</th>
              <th className="text-start px-6 py-3 font-medium">{t("users.companyRole")}</th>
              <th className="text-start px-6 py-3 font-medium">{t("users.branchAccess")}</th>
              <th className="text-start px-6 py-3 font-medium">{t("status.status")}</th>
              <th className="text-start px-6 py-3 font-medium">{t("users.joined")}</th>
              <th className="text-end px-6 py-3 font-medium">{t("invoices.table.actions")}</th>
            </tr>
          </thead>

          <tbody>
            {members.map((member) => {
              const memberId = getMemberId(member);
              const isOwner = member.companyRole === "OWNER";

              return (
                <tr
                  key={memberId}
                  className="border-t border-border hover:bg-muted/30 transition-colors"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-secondary-foreground">
                          {member?.user?.name
                            ?.split(" ")
                            ?.map((part) => part?.[0])
                            ?.join("")
                            ?.toUpperCase() || "U"}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-foreground">
                          {member?.user?.name || "Unnamed User"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {member?.user?.email || "—"}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <RoleBadge>{formatRole(member.companyRole)}</RoleBadge>
                  </td>
                  <td className="px-6 py-4">
                    <BranchAccessList
                      items={member.branchAccess}
                      companyRole={member.companyRole}
                    />
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge status={member.status} />
                  </td>
                  <td className="px-6 py-4 text-muted-foreground">
                    {formatDate(member.createdAt)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end gap-2">
                      {canManage && !isOwner && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onEdit(member)}
                          >
                            {t("actions.edit")}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onRemove(member)}
                            disabled={isRemoving}
                          >
                            {t("users.removeFromCompany", { defaultValue: "Remove from company" })}
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const InvitationsTable = ({
  invitations,
  isError,
  error,
  status,
  onStatusChange,
  onResend,
  onRevoke,
  isResending,
  isRevoking,
}) => {
  const { t } = useTranslation("pages");

  if (isError) {
    const isForbidden =
      error?.status === 403 || error?.response?.status === 403;

    return (
      <div className="bg-card border border-border rounded-xl p-8 text-center">
        <Icon
          name={isForbidden ? "ShieldAlert" : "AlertTriangle"}
          size={32}
          className="mx-auto text-muted-foreground mb-3"
        />
        <p className="text-muted-foreground">
          {isForbidden
            ? t("users.invitationsForbidden", { defaultValue: "You do not have permission to view invitations." })
            : error?.message || t("users.invitationsFailed", { defaultValue: "Failed to load invitations." })}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-xl p-6 shadow-sm border border-border">
        <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              {t("users.invitations", { defaultValue: "Invitations" })}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {t("users.invitationsDescription", { defaultValue: "Track pending, accepted, expired, and revoked invitations." })}
            </p>
          </div>

          <select
            value={status}
            onChange={(event) => onStatusChange(event.target.value)}
            className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
          >
            <option value="">{t("users.allStatuses", { defaultValue: "All statuses" })}</option>
            <option value="PENDING">{t("status.pending")}</option>
            <option value="ACCEPTED">{t("users.accepted", { defaultValue: "Accepted" })}</option>
            <option value="EXPIRED">{t("users.expired", { defaultValue: "Expired" })}</option>
            <option value="REVOKED">{t("users.revoked", { defaultValue: "Revoked" })}</option>
          </select>
        </div>
      </div>

      {invitations.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <Icon
            name="Mail"
            size={32}
            className="mx-auto text-muted-foreground mb-3"
          />
          <h3 className="text-lg font-semibold text-foreground mb-2">
            {t("users.noInvitations")}
          </h3>
          <p className="text-muted-foreground">
            {t("users.invitationsEmptyDescription", { defaultValue: "Invitations will appear here after they are sent." })}
          </p>
        </div>
      ) : (
        <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  <th className="text-start px-6 py-3 font-medium">{t("users.email")}</th>
                  <th className="text-start px-6 py-3 font-medium">{t("users.role")}</th>
                  <th className="text-start px-6 py-3 font-medium">
                    {t("users.branchRoles")}
                  </th>
                  <th className="text-start px-6 py-3 font-medium">{t("status.status")}</th>
                  <th className="text-start px-6 py-3 font-medium">
                    {t("users.invitedBy")}
                  </th>
                  <th className="text-start px-6 py-3 font-medium">
                    {t("users.acceptedBy", { defaultValue: "Accepted By" })}
                  </th>
                  <th className="text-start px-6 py-3 font-medium">{t("users.expires")}</th>
                  <th className="text-end px-6 py-3 font-medium">{t("invoices.table.actions")}</th>
                </tr>
              </thead>

              <tbody>
                {invitations.map((invitation) => {
                  const invitationId = getInvitationId(invitation);
                  const isPending = invitation.status === "PENDING";

                  return (
                    <tr
                      key={invitationId}
                      className="border-t border-border hover:bg-muted/30 transition-colors"
                    >
                      <td className="px-6 py-4 font-medium text-foreground">
                        {invitation.email}
                      </td>
                      <td className="px-6 py-4">
                        <RoleBadge>
                          {formatRole(invitation.companyRole)}
                        </RoleBadge>
                      </td>
                      <td className="px-6 py-4">
                        <BranchAccessList
                          items={invitation.branchRoles}
                          companyRole={invitation.companyRole}
                        />
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge status={invitation.status} />
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">
                        {invitation?.invitedBy?.email ||
                          invitation?.invitedBy?.name ||
                          "—"}
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">
                        {invitation?.acceptedBy?.email ||
                          invitation?.acceptedBy?.name ||
                          "—"}
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">
                        {formatDate(invitation.expiresAt)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex justify-end gap-2">
                          {isPending && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onResend(invitation)}
                                disabled={isResending}
                              >
                                {t("users.resend", { defaultValue: "Resend" })}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onRevoke(invitation)}
                                disabled={isRevoking}
                              >
                                {t("users.revoke", { defaultValue: "Revoke" })}
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const LoadingContent = ({ isCollapsed }) => (
  <div
    className={`transition-all duration-300 ease-in-out ${
      isCollapsed ? "md:ms-16" : "md:ms-64"
    }`}
  >
    <main className="p-4 md:p-6">
      <div className="animate-pulse">
        <div className="mb-8">
          <div className="h-8 bg-muted rounded w-64 mb-2"></div>
          <div className="h-4 bg-muted rounded w-96"></div>
        </div>

        <div className="bg-card rounded-xl p-6 shadow-sm border border-border mb-6">
          <div className="h-10 bg-muted rounded w-64"></div>
        </div>

        <div className="bg-card rounded-xl shadow-sm border border-border">
          <div className="p-6">
            <div className="h-6 bg-muted rounded w-48 mb-4"></div>
            <div className="space-y-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="h-10 w-10 bg-muted rounded-full"></div>
                  <div className="h-4 bg-muted rounded w-36"></div>
                  <div className="h-4 bg-muted rounded w-48"></div>
                  <div className="h-4 bg-muted rounded w-28"></div>
                  <div className="h-4 bg-muted rounded w-20"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
);

const UserManagement = () => {
  return (
    <SidebarProvider>
      <div className="min-h-screen bg-background">
        <Sidebar />
        <UserManagementContent />
      </div>
    </SidebarProvider>
  );
};

export default UserManagement;
